/**
 * PSV-1727 — per-stat upgrades (Starblast-style hotkeys 1–8) + energy.
 *
 * Replaces the single Upgrade button of PSV-1338: eight stats, each
 * levelled 0..MAX_STAT_LEVEL with gems, hotkeys 1–8. Levels scale the
 * BASE VALUES OF THE CURRENT SHIP CLASS (shipClasses.ts), so classes
 * stay meaningful: a maxed Fly is still slower than a fresh Delta.
 * Tier-up (PSV-1726 ChooseShipClass) requires all eight at max and
 * RESETS the levels — the original's loop.
 *
 * Shared between server (validating authority: UpgradeStat RPC, energy
 * gate on fire, regen tick) and client (panel rendering, prediction,
 * local energy gate). Single source of truth, mirroring upgrades.ts /
 * shipClasses.ts.
 */

import type { ShipClassDef } from './shipClasses.js';

/** Stat ids in HOTKEY ORDER — index 0 ↔ key "1" … index 7 ↔ key "8". */
export const STAT_ORDER = [
  'shieldCap',
  'shieldRegen',
  'energyCap',
  'energyRegen',
  'speed',
  'agility',
  'damage',
  'mining',
] as const;

export type StatId = (typeof STAT_ORDER)[number];

export type StatLevels = Record<StatId, number>;

export interface StatDef {
  /** Row text in the stat panel (short enough to fit). */
  label: string;
  /** Tooltip effect line — keep in lockstep with *_PER_LEVEL. */
  effect: string;
  id: StatId;
  /** 1-based hotkey digit, matches STAT_ORDER position. */
  hotkey: number;
  name: string;
  /** Two/three-letter label for the panel cells. */
  short: string;
  desc: string;
}

// PSV-1902 UX — `label` is the row text (fits the panel), `desc`/`effect`
// feed the hover tooltip. Keep effect strings in lockstep with the
// *_PER_LEVEL constants below.
export const STAT_DEFS: readonly StatDef[] = [
  { id: 'shieldCap', hotkey: 1, name: 'Shield capacity', short: 'SHD', label: 'Shield', desc: 'Maximum hull HP', effect: '+10% max HP per level' },
  { id: 'shieldRegen', hotkey: 2, name: 'Shield regen', short: 'RGN', label: 'Sh.Regen', desc: 'Passive HP regeneration', effect: '+0.5 HP/s per level' },
  { id: 'energyCap', hotkey: 3, name: 'Energy capacity', short: 'ENE', label: 'Energy', desc: 'Energy pool — every shot costs 10 per barrel', effect: '+10% energy per level' },
  { id: 'energyRegen', hotkey: 4, name: 'Energy regen', short: 'ERG', label: 'En.Regen', desc: 'Energy recharge rate', effect: '+12.5% regen per level' },
  { id: 'speed', hotkey: 5, name: 'Ship speed', short: 'SPD', label: 'Speed', desc: 'Top speed', effect: '+6% speed per level' },
  { id: 'agility', hotkey: 6, name: 'Agility', short: 'AGI', label: 'Agility', desc: 'Turn rate', effect: '+6% turn rate per level' },
  { id: 'damage', hotkey: 7, name: 'Weapon damage', short: 'DMG', label: 'Damage', desc: 'Damage per projectile (vs ships)', effect: '+10% damage per level' },
  { id: 'mining', hotkey: 8, name: 'Mining speed', short: 'MIN', label: 'Mining', desc: 'Damage vs asteroids', effect: '+15% mining per level' },
] as const;

/** Max level per stat within a tier (original Starblast uses 8). */
export const MAX_STAT_LEVEL = 8;

/**
 * Gems to buy level N (index N-1). Same curve for every stat — the
 * interesting decision is WHICH stat, not comparing curves. Full stat =
 * 107 gems; all eight maxed = 856 — a deliberate demo-scale grind
 * (mining yields 3-20 gems per asteroid) that still gates tier-up
 * meaningfully. Rebalance in one place.
 */
export const STAT_COST_BY_LEVEL: readonly number[] = [2, 4, 6, 9, 13, 18, 24, 31];

/** Cost of raising `statId` FROM `currentLevel` (null when maxed). */
/**
 * PSV-1906 balance — stat costs SCALE WITH THE SHIP TIER (the original's
 * 40L²-per-tier pacing): a flat curve let a player re-max all 8 stats at
 * T4/T5 in minutes and exhaust the whole tree in an hour (PO playtest,
 * 2026-07-28). Index = tier (1-based).
 */
// Index = tier (1-based; index 0 unused). T1/T2 keep the original curve
// so the first hour is unchanged; the re-max grind scales from T3 up.
export const STAT_COST_TIER_MULT: readonly number[] = [1, 1, 1, 2, 3.5, 5];

export function nextStatCost(currentLevel: number, tier = 1): number | null {
  if (currentLevel >= MAX_STAT_LEVEL) return null;
  const base = STAT_COST_BY_LEVEL[currentLevel];
  if (base === undefined) return null;
  const mult = STAT_COST_TIER_MULT[Math.min(tier, STAT_COST_TIER_MULT.length - 1)] ?? 1;
  return Math.round(base * mult);
}

export function emptyStatLevels(): StatLevels {
  return {
    shieldCap: 0,
    shieldRegen: 0,
    energyCap: 0,
    energyRegen: 0,
    speed: 0,
    agility: 0,
    damage: 0,
    mining: 0,
  };
}

/** Tolerant parse of a persisted/wire statLevels blob (JSON or object). */
export function parseStatLevels(raw: unknown): StatLevels {
  const out = emptyStatLevels();
  let obj: unknown = raw;
  if (typeof raw === 'string' && raw !== '') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return out;
    }
  }
  if (obj === null || typeof obj !== 'object') return out;
  for (const id of STAT_ORDER) {
    const v = (obj as Record<string, unknown>)[id];
    if (typeof v === 'number' && Number.isFinite(v)) {
      out[id] = Math.max(0, Math.min(MAX_STAT_LEVEL, Math.floor(v)));
    }
  }
  return out;
}

export function allStatsMaxed(levels: StatLevels): boolean {
  return STAT_ORDER.every((id) => levels[id] >= MAX_STAT_LEVEL);
}

// ---------------------------------------------------------------------------
// Effective stats — class base × level scaling. BOTH sims consume this;
// keep it pure and deterministic.
// ---------------------------------------------------------------------------

/** Baseline energy pool/regen before class/level scaling. */
export const BASE_ENERGY_CAP = 100;
export const BASE_ENERGY_REGEN_PER_S = 12;
/** Energy drained per PROJECTILE (a 3-barrel volley costs 3×). */
export const ENERGY_COST_PER_PROJECTILE = 10;

/** Per-level multipliers/additions (level 8 shown in comments). */
const SHIELD_CAP_PER_LEVEL = 0.1; // ×1.8 hpMax
const SHIELD_REGEN_PER_LEVEL_HP_S = 0.5; // 4 hp/s
const ENERGY_CAP_PER_LEVEL = 0.1; // ×1.8
const ENERGY_REGEN_PER_LEVEL = 0.125; // ×2.0
const SPEED_PER_LEVEL = 0.06; // ×1.48 engineMax
const AGILITY_PER_LEVEL = 0.06; // ×1.48 rad/s
const DAMAGE_PER_LEVEL = 0.1; // ×1.8 per projectile
const MINING_PER_LEVEL = 0.15; // ×2.2 vs asteroids

export interface EffectiveStats {
  hpMax: number;
  /** Passive HP regen, hp/s (0 at level 0). */
  shieldRegenPerS: number;
  energyCap: number;
  energyRegenPerS: number;
  engineMax: number;
  agility: number;
  weaponDmg: number;
  /** Extra damage multiplier applied ONLY to asteroid hits. */
  miningMult: number;
}

/**
 * Combine a ship class's base profile with stat levels. Values are
 * rounded where gameplay compares integers (hp, damage) and left float
 * where physics integrates (speeds, regens).
 */
export function effectiveStats(
  shipClass: ShipClassDef,
  levels: StatLevels,
): EffectiveStats {
  return {
    hpMax: Math.round(shipClass.hpMax * (1 + SHIELD_CAP_PER_LEVEL * levels.shieldCap)),
    shieldRegenPerS: SHIELD_REGEN_PER_LEVEL_HP_S * levels.shieldRegen,
    energyCap: Math.round(BASE_ENERGY_CAP * (1 + ENERGY_CAP_PER_LEVEL * levels.energyCap)),
    energyRegenPerS:
      BASE_ENERGY_REGEN_PER_S * (1 + ENERGY_REGEN_PER_LEVEL * levels.energyRegen),
    engineMax: shipClass.engineMax * (1 + SPEED_PER_LEVEL * levels.speed),
    agility: shipClass.agility * (1 + AGILITY_PER_LEVEL * levels.agility),
    weaponDmg: Math.round(shipClass.weapon.damage * (1 + DAMAGE_PER_LEVEL * levels.damage)),
    miningMult: 1 + MINING_PER_LEVEL * levels.mining,
  };
}

/**
 * Server-side purchase validation (client mirrors it for the panel
 * affordability glow). Returns null when legal, else a stable reason.
 */
export function validateStatUpgrade(
  levels: StatLevels,
  gems: number,
  statId: string,
  tier = 1,
): string | null {
  if (!STAT_ORDER.includes(statId as StatId)) return 'unknown stat';
  const cost = nextStatCost(levels[statId as StatId], tier);
  if (cost === null) return 'stat already maxed';
  if (gems < cost) return 'not enough gems';
  return null;
}
